import React from "react";
import LegalPage from "../../components/LegalPage";

export default function TermsConditions() {
  return (
    <LegalPage
      testid="legal-terms"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Terms of Service"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="PLEASE READ THESE TERMS CAREFULLY. BY COMPLETING A PURCHASE OR ACTIVATING AN ESIM, YOU AGREE TO BE LEGALLY BOUND BY THESE TERMS, WHEREVER YOU ARE LOCATED."
    >
      <p className="!mt-2 italic text-ink-700">
        <strong>Notice for users outside the United Kingdom.</strong> BlueRoam sells its eSIM products
        globally from the United Kingdom. These Terms are governed by English law (see clause 18). If
        you are a consumer resident outside the UK, you retain the benefit of any{" "}
        <strong>mandatory</strong> consumer protections in your country of residence, which apply in
        addition to these Terms.
      </p>

      <h2>1. About Us and These Terms</h2>
      <p>
        1.1 These terms of service (the “Terms”) govern your purchase and use of eSIM data plans and
        related services (the “Services”) supplied by BlueRoam Ltd, a company registered in England
        and Wales (company number 17215030), registered office at 128 City Road, London, EC1V 2NX
        (“BlueRoam”, “we”, “us”, “our”), through the website <strong>blueroam.io</strong> and any
        related mobile or web applications (the “Platform”).
      </p>
      <p>1.2 Contact: <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.</p>
      <p>
        1.3 These Terms, together with our <a href="/legal/privacy">Privacy Policy</a>,{" "}
        <a href="/legal/refunds">Refund Policy</a>, <a href="/legal/website-terms">Website Terms of Use</a>,{" "}
        <a href="/legal/aml-kyc">AML/KYC Policy</a>, US Privacy Notice (where applicable), and any
        order confirmation, form the contract between you and BlueRoam (the “Contract”). The order
        confirmation prevails over these Terms; these Terms prevail over the other documents.
      </p>
      <p>
        1.4 We may update these Terms from time to time. The version applicable to your purchase is
        the one in force when you place your order. Material changes are notified by email or via
        the Platform with reasonable notice.
      </p>

      <h2>2. Definitions</h2>
      <ul>
        <li><strong>“Consumer”</strong> means an individual purchasing the Services wholly or mainly outside their trade, business, craft or profession (within the meaning of the UK Consumer Rights Act 2015 and equivalent local-law definitions).</li>
        <li><strong>“Data Plan”</strong> means a specific eSIM product (defined by data allowance, validity period and country/regional coverage).</li>
        <li><strong>“eSIM”</strong> means an embedded SIM profile installed on your compatible device that enables access to mobile data services on partner mobile networks.</li>
        <li><strong>“Order”</strong> means your offer to purchase a Data Plan via the Platform.</li>
        <li><strong>“Partner Networks”</strong> means the third-party mobile network operators whose networks provide the underlying connectivity.</li>
      </ul>

      <h2>3. Eligibility</h2>
      <p>
        3.1 You must be at least <strong>18 years old</strong> (or the age of majority in your country,
        whichever is higher) and have legal capacity to enter into a binding contract.
      </p>
      <p>3.2 By placing an Order, you confirm that:</p>
      <ul>
        <li>(a) you meet the eligibility requirements in clause 3.1;</li>
        <li>(b) the information you provide is accurate and complete;</li>
        <li>(c) you are <strong>not</strong> a resident of, located in, or ordinarily present in any <strong>comprehensively sanctioned jurisdiction</strong> (currently including Cuba, Iran, North Korea, Syria and the Russian-occupied regions of Ukraine), and are <strong>not</strong> a designated person under any applicable sanctions regime (UK, EU, UN, OFAC or other);</li>
        <li>(d) the use of the Services does not breach any law of your country of residence or the destination country; and</li>
        <li>(e) you will use the Services lawfully.</li>
      </ul>
      <p>
        3.3 We screen all transactions for sanctions, fraud and AML risk in accordance with our{" "}
        <a href="/legal/aml-kyc">AML/KYC Policy</a>. We may refuse or cancel any Order at our
        reasonable discretion.
      </p>

      <h2>4. Device Compatibility</h2>
      <p>
        4.1 The Services require a device that supports eSIM and is <strong>unlocked</strong>{" "}
        (not network-locked to a specific carrier).{" "}
        <strong>It is your responsibility to confirm device compatibility before purchasing.</strong>
      </p>
      <p>
        4.2 We publish compatibility guidance on the Platform but do not warrant that any particular
        device works with any particular Data Plan. No refund is given for device incompatibility
        where guidance was published before your purchase, except where required by applicable
        consumer law.
      </p>

      <h2>5. Orders and Contract Formation</h2>
      <p>5.1 Each Order is your offer to purchase on these Terms.</p>
      <p>
        5.2 The Contract is formed when we send an order confirmation containing your QR code or
        activation instructions. Where we cannot accept an Order (stock or pricing error, suspected
        fraud, sanctions screening failure), we notify you and refund any amount charged.
      </p>
      <p>5.3 We reserve the right to refuse, cancel or limit any Order at our reasonable discretion.</p>

      <h2>6. Pricing, Payment, Taxes and Currency</h2>
      <p>
        6.1 Prices are displayed in the currency selected at checkout. Where required by applicable
        law, prices are shown inclusive of applicable VAT, GST, sales tax or equivalent indirect
        taxes; otherwise such taxes are itemised at checkout.
      </p>
      <p>
        6.2 You are responsible for any taxes, duties, levies or charges imposed by your country of
        residence that are not collected at checkout.
      </p>
      <p>
        6.3 Cross-border surcharges or currency-conversion fees imposed by your card issuer are not
        within our control.
      </p>
      <p>6.4 We may change prices at any time; changes do not affect confirmed Orders.</p>
      <p>
        6.5 Payment is taken in full at the time of Order via the payment methods made available at
        checkout. Payment is processed by our third-party payment service provider(s), subject to
        their terms.
      </p>

      <h2>7. Delivery and Activation</h2>
      <p>
        7.1 eSIMs are delivered <strong>digitally</strong> by email and/or via your account,
        typically within minutes of order confirmation. Delivery is complete when the QR code or
        activation details are made available to you.
      </p>
      <p>
        7.2 You are responsible for installation in accordance with the activation instructions and
        any device manufacturer instructions.
      </p>
      <p>
        7.3 <strong>Validity periods commence at the earlier of (i) first network registration in the
        destination country or region, (ii) any explicit activation date selected at checkout, or
        (iii) the activation trigger described in the Data Plan listing.</strong> Once activated,
        validity periods run continuously and cannot be paused.
      </p>

      <h2>8. Service Description; No Guarantee of Coverage</h2>
      <p>
        8.1 BlueRoam is a <strong>reseller of mobile data services</strong> provided by Partner
        Networks. We do not own or operate mobile networks.
      </p>
      <p>
        8.2 Coverage, speed, latency and network technology (3G/4G/5G) depend on Partner Networks and
        vary by location and over time. Coverage maps are indicative only and{" "}
        <strong>do not constitute a warranty of coverage at any specific location</strong>.
      </p>
      <p>
        8.3 The Services provide <strong>data only</strong> unless expressly stated otherwise. Voice,
        SMS, MMS, USSD, tethering, VoIP and emergency-services calling (including 999/112/911 and
        equivalents) may be unavailable, restricted or chargeable.{" "}
        <strong>Do not rely on the Services for emergency communications.</strong>
      </p>
      <p>
        8.4 Service may be interrupted or degraded for reasons including network maintenance,
        congestion, force majeure, regulatory action or upstream supplier failure. Service may also
        be restricted or shut down by acts of local government in the destination country (including
        content blocks, VoIP restrictions or internet shutdowns).
      </p>

      <h2>9. Acceptable Use</h2>
      <p>9.1 You must not use the Services:</p>
      <ul>
        <li>(a) for any unlawful purpose under the laws of the UK, your country of residence or the destination country;</li>
        <li>(b) in breach of any applicable sanctions or export-control regime;</li>
        <li>(c) in a manner that is excessive, abusive or constitutes a denial-of-service to other users (a “fair use” cap may apply and will be set out in the Data Plan listing);</li>
        <li>(d) to operate bulk SMS, telemarketing, machine-to-machine (M2M) or IoT services without our prior written consent;</li>
        <li>(e) to bypass, modify, reverse engineer or interfere with the Platform or any Partner Network;</li>
        <li>(f) to resell the Services without our prior written authorisation.</li>
      </ul>
      <p>
        9.2 We may suspend or terminate the Services without refund if we reasonably believe you have
        breached this clause.
      </p>

      <h2>10. Cancellation, Refunds and Statutory Rights</h2>
      <p>
        10.1 Cancellation and refund rights are set out in our{" "}
        <a href="/legal/refunds">Refund Policy</a>, which forms part of these Terms and includes
        specific provisions for UK consumers, EU consumers and consumers in certain other
        jurisdictions.
      </p>
      <p>
        10.2 <strong>UK and EU Consumers:</strong> distance-selling cancellation rights apply, but are{" "}
        <strong>lost</strong> in respect of digital content once supply has begun with your express
        prior consent and acknowledgment that the right is lost. By electing to receive your eSIM
        immediately at checkout, you provide such consent.
      </p>
      <p>
        10.3 Nothing in these Terms affects your <strong>mandatory</strong> statutory rights as a
        Consumer in your country of residence (including, in the UK, the Consumer Rights Act 2015;
        in the EU, the Consumer Rights Directive and Digital Content Directive; and equivalent
        rights in other jurisdictions).
      </p>

      <h2>11. Faulty or Misdescribed Services</h2>
      <p>
        11.1 If the Services are faulty or do not conform to their description, contact us as soon as
        reasonably practicable at <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> with
        details and, where relevant, diagnostic information (screenshots, device model, location,
        error messages).
      </p>
      <p>
        11.2 We investigate in good faith and, where the issue is verified and within our control or
        that of our Partner Networks, provide an appropriate remedy in accordance with the Refund
        Policy and applicable consumer law.
      </p>

      <h2>12. Intellectual Property</h2>
      <p>
        12.1 All intellectual property in the Platform, including the <strong>BlueRoam</strong> name
        and logo, is owned by or licensed to BlueRoam.
      </p>
      <p>
        12.2 You receive a limited, non-exclusive, non-transferable licence to use the Platform and
        Services for their intended purpose; no other rights are granted.
      </p>

      <h2>13. Data Protection and Privacy</h2>
      <p>
        13.1 Your personal data is processed in accordance with our{" "}
        <a href="/legal/privacy">Privacy Policy</a>.
      </p>
      <p>
        13.2 If you are located in <strong>California or another US state</strong> with a
        comprehensive consumer-privacy law, additional rights and disclosures apply as set out in
        our US Privacy Notice.
      </p>
      <p>
        13.3 If you are located in the <strong>EEA, Switzerland or the United Kingdom</strong>, the
        UK GDPR and/or EU GDPR apply, and you have the rights set out in the Privacy Policy.
      </p>

      <h2>14. Liability</h2>
      <p>
        14.1 Nothing in these Terms limits or excludes liability that cannot be limited or excluded
        under applicable law, including liability for: (a) death or personal injury caused by
        negligence; (b) fraud or fraudulent misrepresentation; or (c) any other liability that
        cannot be limited or excluded by law (including statutory rights under the UK Consumer
        Rights Act 2015 and equivalent foreign consumer-protection legislation).
      </p>
      <p>
        14.2 Subject to clause 14.1, BlueRoam’s <strong>total aggregate liability</strong> to you
        arising under or in connection with the Contract, whether in contract, tort (including
        negligence), breach of statutory duty or otherwise, is limited to{" "}
        <strong>
          the greater of (a) the total amount paid by you for the relevant Data Plan in the 12
          months preceding the event giving rise to liability, and (b) £100 (or its equivalent in
          your transaction currency)
        </strong>.
      </p>
      <p>14.3 Subject to clause 14.1, BlueRoam is not liable for:</p>
      <ul>
        <li>(a) loss of profits, revenue, business, anticipated savings, opportunity or goodwill;</li>
        <li>(b) loss or corruption of data;</li>
        <li>(c) indirect, special, incidental, punitive or consequential loss;</li>
        <li>(d) loss arising from Partner Network outages or coverage limitations beyond BlueRoam’s reasonable control;</li>
        <li>(e) loss arising from your use of, or inability to use, the Services in connection with emergency communications;</li>
        <li>(f) loss arising from acts of government in any destination country, including internet shutdowns, content blocking or service restrictions.</li>
      </ul>
      <p>14.4 If you are not a Consumer, the exclusions in clause 14.3 apply to the maximum extent permitted by law.</p>

      <h2>15. Suspension and Termination</h2>
      <p>
        15.1 We may suspend or terminate the Services and/or your account immediately on notice if
        you materially breach these Terms or if required by law, regulation, sanctions screening or
        our Partner Networks.
      </p>
      <p>15.2 Termination does not affect any rights or remedies accrued up to the date of termination.</p>

      <h2>16. Force Majeure</h2>
      <p>
        Neither party is liable for any failure or delay in performance caused by events beyond its
        reasonable control, including acts of God, war, terrorism, civil unrest, pandemic,
        governmental action, network failure, cyber attack, internet shutdown or industrial dispute.
      </p>

      <h2>17. Complaints and Alternative Dispute Resolution</h2>
      <p>
        17.1 If you have a complaint, contact <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
        We aim to acknowledge complaints within <strong>5 business days</strong> and resolve them
        within <strong>30 days</strong>.
      </p>
      <p>
        17.2 <strong>UK Consumers:</strong> if we cannot resolve a complaint, you may have the right
        to refer the matter to an Alternative Dispute Resolution (“ADR”) body. We are not currently
        subscribed to a mandatory ADR scheme.
      </p>
      <p>
        17.3 <strong>EU Consumers:</strong> you may use the EU{" "}
        <strong>Online Dispute Resolution</strong> platform at{" "}
        <a href="https://ec.europa.eu/odr" target="_blank" rel="noreferrer">https://ec.europa.eu/odr</a>{" "}
        to submit complaints.
      </p>
      <p>
        17.4 <strong>Consumers elsewhere:</strong> you may have rights to refer disputes to local
        consumer-protection authorities or ADR bodies in your country of residence.
      </p>

      <h2>18. Governing Law and Jurisdiction</h2>
      <p>18.1 These Terms and the Contract are governed by the laws of <strong>England and Wales</strong>.</p>
      <p>
        18.2 The courts of <strong>England and Wales</strong> have exclusive jurisdiction to settle
        any dispute, <strong>save that</strong>:
      </p>
      <ul>
        <li>(a) if you are a Consumer resident in <strong>Scotland or Northern Ireland</strong>, you may bring proceedings in your local courts;</li>
        <li>(b) if you are a Consumer resident in the <strong>EEA, Switzerland or another jurisdiction</strong> that gives consumers the right to sue in their local courts and/or under local law (e.g., under Article 6 of the Rome I Regulation, EU Brussels I (Recast) Regulation or equivalent), you retain that right; and</li>
        <li>(c) nothing in this clause limits the application of <strong>mandatory</strong> consumer-protection laws of your country of residence.</li>
      </ul>

      <h2>19. US Customers — Additional Provisions</h2>
      <p>19.1 If you are a US resident, the following supplement these Terms:</p>
      <ul>
        <li>(a) <strong>No class actions.</strong> To the maximum extent permitted by law, you and BlueRoam agree to resolve disputes on an individual basis and not as part of any class, consolidated or representative action. [Note: enforceability varies state to state; review with counsel.]</li>
        <li>(b) <strong>Choice of forum.</strong> Mandatory federal or state consumer-protection laws of your state of residence will apply to the extent they cannot be contractually waived; this does not displace clause 18 for any other purpose.</li>
        <li>(c) <strong>California users:</strong> see our US Privacy Notice for California-specific rights, including under the CCPA/CPRA.</li>
      </ul>

      <h2>20. General</h2>
      <p>20.1 <strong>Assignment.</strong> You may not assign the Contract without our consent. We may assign or novate it to any affiliate or successor on notice.</p>
      <p>20.2 <strong>Entire agreement.</strong> The Contract is the entire agreement between you and us and supersedes any prior agreement on its subject matter.</p>
      <p>20.3 <strong>Severability.</strong> If any provision is held unenforceable, the remainder continues in full force.</p>
      <p>20.4 <strong>No waiver.</strong> No failure or delay in exercising a right is a waiver.</p>
      <p>20.5 <strong>Third-party rights.</strong> A person who is not a party has no right to enforce the Contract under the UK Contracts (Rights of Third Parties) Act 1999 or equivalent legislation.</p>
      <p>20.6 <strong>Notices.</strong> Notices to you may be sent to the email associated with your account. Notices to us: <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> with a copy to our registered office.</p>
      <p>20.7 <strong>Language.</strong> These Terms are issued in English. Any translation is provided for convenience only; the English version prevails in case of conflict, except where applicable mandatory law requires otherwise.</p>

      <p className="!mt-10">
        Questions: <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
      </p>
    </LegalPage>
  );
}
